<template>
  <table-wrapper>
    <v-data-table-server
      class="coronet-table--highlightable backup-and-restore-child-workspaces-table"
      :items="items"
      :loading="loading"
      fixed-header
      :no-data-text="$t('general.noDataAvailable')"
      :page="pagination.page + 1"
      :items-per-page="pagination.pageSize"
      :items-length="totalItems"
      @update:options="
        $emit('page-changed', {
          page: $event.page - 1,
          pageSize: $event.itemsPerPage,
        })
      "
    >
      <template #headers>
        <tr>
          <th class="w-20">
            {{ $t("cloudBackupAndRestore.childWorkspacesTable.headers.workspaceName") }}
          </th>
          <th class="w-15">
            {{ $t("cloudBackupAndRestore.childWorkspacesTable.headers.cloudApps") }}
          </th>
          <th class="w-15">
            {{ $t("cloudBackupAndRestore.childWorkspacesTable.headers.connectionStatus") }}
          </th>
          <th class="w-10">
            {{ $t("cloudBackupAndRestore.childWorkspacesTable.headers.healthStatus") }}
          </th>
          <th class="w-15">
            {{ $t("cloudBackupAndRestore.childWorkspacesTable.headers.backedUpUsers") }}
          </th>
          <th v-if="showActions" class="w-10" />
        </tr>
      </template>
      <template #item="{ item, index }">
        <tr :key="index" :data-testid="`backup-and-restore-child-workspaces-table-row-${index}`">
          <td :data-testid="`backup-and-restore-child-workspaces-table-name-col-row-${index}`">
            <div class="d-flex align-center">
              <v-icon size="40" class="mr-3 child-plan-icon">icon-child-plan-s</v-icon>
              <span class="subtitle2">{{ item.name }}</span>
            </div>
          </td>
          <td
            :data-testid="`backup-and-restore-child-workspaces-table-cloud-apps-col-row-${index}`"
          >
            <div class="d-flex align-center ga-1">
              <template v-for="workload in item.workloads" :key="workload.name">
                <img
                  v-if="workloadIcons[workload.name]"
                  :src="`/images/app-service-logos/${workloadIcons[workload.name]}`"
                  :alt="$t(`general.keepitWorkloads.${workload.name}`)"
                  :class="{ 'workload-icon--disconnected': !isWorkloadConnected(workload) }"
                  class="workload-icon"
                />
              </template>
            </div>
          </td>
          <td
            :data-testid="`backup-and-restore-child-workspaces-table-connection-col-row-${index}`"
          >
            <div class="d-flex align-center ga-1">
              <span class="body2" :class="getConnectionStatus(item).colorClass">
                {{ getConnectionStatus(item).text }}
              </span>
              <v-tooltip v-if="getConnectionStatus(item).showInfo" location="top" max-width="320">
                <template #activator="{ props }">
                  <v-icon
                    v-bind="props"
                    size="18"
                    icon="$infoXs"
                    class="connection-info-icon"
                    :class="getConnectionStatus(item).colorClass"
                  />
                </template>
                {{ $t("cloudBackupAndRestore.childWorkspacesTable.connectionTooltip") }}
              </v-tooltip>
            </div>
          </td>
          <td :data-testid="`backup-and-restore-child-workspaces-table-health-col-row-${index}`">
            <span class="body2" :class="getHealthStatus(item).colorClass">
              {{ getHealthStatus(item).text }}
            </span>
          </td>
          <td :data-testid="`backup-and-restore-child-workspaces-table-usage-col-row-${index}`">
            <span>{{ getUsageSummary(item).usage }}/</span>
            <span class="text-indigo-medium">{{ getUsageSummary(item).limit }}</span>
          </td>
          <td v-if="showActions" class="text-right coro-table-col--sticky-right">
            <v-menu bottom left>
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  icon="$dots"
                  class="item-clickable"
                  :data-testid="`backup-and-restore-child-workspaces-table-actions-${index}`"
                />
              </template>
              <v-list>
                <v-list-item
                  v-for="action in itemActions"
                  :key="action"
                  @click="$emit('action-applied', { action, item })"
                >
                  <v-list-item-title
                    :data-testid="`backup-and-restore-child-workspaces-table-${action}-action-${index}`"
                  >
                    {{ $t(`cloudBackupAndRestore.childWorkspacesTable.actions.${action}`) }}
                  </v-list-item-title>
                  <template v-if="action === BackupChildAction.VIEW_IN_KEEPIT" #append>
                    <v-icon
                      size="18"
                      class="ml-2"
                      icon="$externalLink"
                      :data-testid="`backup-and-restore-child-workspaces-table-${action}-icon-${index}`"
                    />
                  </template>
                </v-list-item>
              </v-list>
            </v-menu>
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import TableWrapper from "@/components/TableWrapper.vue";
import {
  KEEPIT_WORKLOAD_ICONS,
  BackupChildAction,
  BackupChildConnectionStatus,
  BackupChildHealthStatus,
  getBackupChildConnectionStatus,
  getBackupChildHealthStatus,
  isWorkloadConnected,
  type BackupChild,
} from "@/constants/cloud-backup.ts";
import type { Pagination } from "@/types";

interface StatusMeta {
  text: string;
  colorClass: string;
  showInfo?: boolean;
}

export default defineComponent({
  components: { TableWrapper },
  props: {
    items: {
      type: Array as PropType<BackupChild[]>,
      required: true,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      required: true,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showActions: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["action-applied", "page-changed"],
  setup() {
    const i18n = useI18n();

    const getConnectionStatus = (item: BackupChild): StatusMeta => {
      const status = getBackupChildConnectionStatus(item);

      if (status === BackupChildConnectionStatus.CONNECTED) {
        return {
          text: i18n.t("cloudBackupAndRestore.childWorkspacesTable.connectionStatus.connected"),
          colorClass: "text-green-dark",
        };
      }
      if (status === BackupChildConnectionStatus.PARTIAL) {
        return {
          text: i18n.t("cloudBackupAndRestore.childWorkspacesTable.connectionStatus.partial", {
            connected: item.workloads.filter(isWorkloadConnected).length,
            total: item.workloads.length,
          }),
          colorClass: "text-yellow-base",
          showInfo: true,
        };
      }
      return {
        text: i18n.t("cloudBackupAndRestore.childWorkspacesTable.connectionStatus.notConnected"),
        colorClass: "text-red-dark",
        showInfo: true,
      };
    };

    const getHealthStatus = (item: BackupChild): StatusMeta => {
      const healthMetaMap: Record<BackupChildHealthStatus, StatusMeta> = {
        [BackupChildHealthStatus.NONE]: {
          text: i18n.t("cloudBackupAndRestore.childWorkspacesTable.healthStatus.none"),
          colorClass: "text-indigo-medium",
        },
        [BackupChildHealthStatus.HEALTHY]: {
          text: i18n.t("cloudBackupAndRestore.childWorkspacesTable.healthStatus.healthy"),
          colorClass: "text-green-dark",
        },
        [BackupChildHealthStatus.UNHEALTHY]: {
          text: i18n.t("cloudBackupAndRestore.childWorkspacesTable.healthStatus.unhealthy"),
          colorClass: "text-yellow-base",
        },
        [BackupChildHealthStatus.CRITICAL]: {
          text: i18n.t("cloudBackupAndRestore.childWorkspacesTable.healthStatus.critical"),
          colorClass: "text-red-dark",
        },
      };

      return healthMetaMap[getBackupChildHealthStatus(item)];
    };

    const getUsageSummary = (item: BackupChild): { usage: number; limit: number } => ({
      usage: item.workloads.reduce((sum, workload) => sum + workload.usage, 0),
      limit: item.workloads.reduce((sum, workload) => sum + workload.limit, 0),
    });

    return {
      workloadIcons: KEEPIT_WORKLOAD_ICONS,
      BackupChildAction,
      itemActions: [BackupChildAction.VIEW_IN_KEEPIT, BackupChildAction.EDIT],
      isWorkloadConnected,
      getConnectionStatus,
      getHealthStatus,
      getUsageSummary,
    };
  },
});
</script>

<style scoped lang="scss">
.v-table {
  border: 1px solid rgb(var(--v-theme-indigo-faint)) !important;
  box-shadow: none !important;
}

.connection-info-icon:before {
  color: inherit !important;
}

.child-plan-icon {
  color: rgb(var(--v-theme-indigo-medium));
}

.workload-icon {
  width: 24px;
  height: 24px;

  &--disconnected {
    opacity: 0.5;
  }
}
</style>
